import { store, store$ } from '@redux/store';
import fastdom from 'fastdom';
import { debounceTime, distinctUntilChanged, filter, map, merge, of, skip, switchMap, tap } from "rxjs";
import { selectPage, selectPageSize, onArticleCountResultsLoaded$ } from 'features/articleSearchResultsFacets';
import { routingSelectors, buildNewUrl, getPaging } from 'features/routing';

const paginationContainer = document.querySelector('[data-component="article-search-results-pagination"]');
const previousButton = paginationContainer.querySelector('[data-prev-button]');
const nextButton = paginationContainer.querySelector('[data-next-button]');
const optionList = paginationContainer.querySelector('[data-option-list]');
const { urlComponentsSelector } = routingSelectors;

/**
 * Retrieves the total count of results from the pagination container.
 * @returns {number} The total count of results.
 */
const getCountResults = () => Number(paginationContainer?.dataset?.countResults || 0);

/**
 * Builds the URL for a specific page.
 * @param {number} page - The page number.
 * @param {Object} urlComponents - The URL components.
 * @returns {string} The constructed URL for the page.
 */
const getPageLinkUrl = function(page, urlComponents) {
    const pagingSegment = getPaging(page);
    const urlSegments = { ...urlComponents, urlPaging: pagingSegment };
    return buildNewUrl(urlSegments);
};

/**
 * Generates a list of page numbers to display in the pagination bar.
 * @param {number} totalPages - The total number of pages.
 * @param {number} currentPage - The current page number.
 * @returns {Array<number>} The list of page numbers.
 */
const getPageNumberList = function (totalPages, currentPage) {
    const pages = [];
    const maximumPagesToShow = 7;

    if (maximumPagesToShow >= totalPages) {
        return [...Array(totalPages).keys()].map((index) => index + 1);
    }

    const firstPage = 1;
    const lastPage = totalPages;
    pages.push(firstPage);

    const pageNumberToRight = lastPage - currentPage;
    const pageNumberToLeft = currentPage - firstPage;

    if (pageNumberToLeft > 3 && pageNumberToRight > 3) {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
    }

    if (pageNumberToLeft <= 3) {
        pages.push(firstPage + 1, firstPage + 2, firstPage + 3, firstPage + 4);
    }

    if (pageNumberToRight <= 3) {
        pages.push(lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1);
    }

    pages.push(lastPage);
    return pages;
};

/**
 * Generates the HTML for the pagination bar.
 * @param {Array<number>} pageNumberList - The list of page numbers.
 * @param {number} currentPage - The current page number.
 * @param {Object} urlComponents - The URL components.
 * @returns {string} The HTML string for the pagination bar.
 */
const getPaginationHtml = function(pageNumberList, currentPage, urlComponents) {
    return pageNumberList.reduce((accumulator, pageNumber, pageIndex) => {
        const previousPageNumber = pageNumberList[pageIndex - 1] || 0;
        const threeDotsElement = previousPageNumber + 1 !== pageNumber
            ? `<li class="nhs-c-pagination__spacer-container nhs-mq-pagination__spacer-container--extra-large"><span>...</span></li>` : '';

        const currentPageClasses = pageNumber === currentPage ? "nhs-c-pagination__number--active" : '';

        const pageLink =
            `<li class="nhs-c-pagination__number-container nhs-mq-pagination__number-container--extra-large" data-qa="pagination_number_container">
                <a data-page-link="${pageNumber}" href="${getPageLinkUrl(pageNumber, urlComponents)}/" aria-label="Go to ${pageNumber} page" class="nhs-c-pagination__number nhs-mq-pagination__number--extra-large ${currentPageClasses}">${pageNumber}</a>
            </li>`;

        return `${accumulator}${threeDotsElement}${pageLink}`;
    }, '');
};

/**
 * Updates the pagination bar in the DOM.
 * @param {Object} params - The parameters for updating the pagination bar.
 * @param {string} params.paginationHtml - The HTML for the pagination bar.
 * @param {number} params.currentPage - The current page number.
 * @param {number} params.totalPages - The total number of pages.
 */
const updatePaginationBar = function({ paginationHtml, currentPage, totalPages }) {
    fastdom.mutate(() => {
        previousButton.disabled = currentPage === 1;
        nextButton.disabled = currentPage === totalPages;
        optionList.innerHTML = paginationHtml;
    });
};

/**
 * Observable that listens for page changes in the Redux store.
 *
 * @type {Observable<number>}
 */
const onPageChange$ = store$.pipe(
    map(selectPage),
    filter(Boolean),
    distinctUntilChanged(),
    map(getCountResults),
    skip(1)
);

/**
 * Observable that listens for page size changes in the Redux store.
 * @type {Observable<number>}
 */
const onPageSizeChange$ = store$.pipe(
    map(selectPageSize),
    filter(Boolean),
    distinctUntilChanged(),
    map(getCountResults),
    skip(1)
);

/**
 * Observable that updates the count results when new article count results are loaded.
 * @type {Observable<number>}
 */
const newCountResults$ = onArticleCountResultsLoaded$.pipe(
    tap((countResults) => { paginationContainer.dataset.countResults = countResults; })
);

/**
 * Observable that listens for pagination changes and updates the pagination bar.
 * @type {Observable<Object>}
 */
const onPaginationChange$ = merge(onPageChange$, onPageSizeChange$, newCountResults$).pipe(
    filter((countResults) => countResults > 20),
    debounceTime(500),
    switchMap((countResults) => {
        const state = store.getState();
        const currentPage = selectPage(state);
        const currentPageSize = selectPageSize(state);
        const urlComponents = urlComponentsSelector(state);
        const totalPages = Math.ceil(countResults / currentPageSize);
        const pageNumberList = getPageNumberList(totalPages, currentPage);
        const paginationHtml = getPaginationHtml(pageNumberList, currentPage, urlComponents);
        return of({ paginationHtml, currentPage, totalPages });
    }),
    distinctUntilChanged()
);

// Subscribe to the pagination change observable to update the pagination bar.
onPaginationChange$.subscribe(updatePaginationBar);